import { MyRouterContext } from '@/App';
import { MainContainer } from '@/components/MainContainer';
import { PageHeader } from '@/components/PageHeader';
import { PendingInitialize } from '@/components/PendingInitialize';
import { colors } from '@/const';
import { trackSignUpEvent } from '@/lib/analytics';
import { logger } from '@/lib/logger';
import { notifyError } from '@/lib/notification';
import {
  Anchor,
  Box,
  Button,
  Radio,
  SimpleGrid,
  Stack,
  Tabs,
  Text,
  useComputedColorScheme,
} from '@mantine/core';
import { useMutation } from '@tanstack/react-query';
import { createFileRoute, LoaderFnContext } from '@tanstack/react-router';
import { useState } from 'react';

const NEW_TENANT = 'new';
const PRICING_URL = 'https://plamo.preferredai.jp/api#pricing';
const CONTACT_FORM_URL =
  'https://docs.google.com/forms/d/e/1FAIpQLScxj2B3ItzI-_fZOZZHePi_LaossUhLhvU7z58lfd3zG0zOVg/viewform';
// light の白文字で 4.5:1 を取るため、ロゴの紫より暗くした塗り。dark はロゴの紫に暗い文字を載せる
const PROVIDER_BUTTON_LIGHT = '#7C56C4';

type PlanKey = 'free' | 'standard' | 'provider';

// 価格は landing_page/src/routes/-components/ApiPricingSection.tsx の pricingCards と揃える
const plans: { key: PlanKey; name: string; price: string; audience: string; color: string }[] = [
  { key: 'free', name: 'Free', price: '無料', audience: 'まず試す', color: colors.plamoBlue2 },
  {
    key: 'standard',
    name: 'Standard',
    price: '60円 / 250円',
    audience: '本番・業務で使う',
    color: 'var(--mantine-primary-color-filled)',
  },
  {
    key: 'provider',
    name: 'Provider',
    price: '個別見積もり',
    audience: 'AI サービスとして提供する',
    color: colors.plamoPurple,
  },
];

export const Route = createFileRoute('/_main/_project/upgrade/')({
  loader,
  component: Component,
  pendingComponent: () => <PendingInitialize title="有料プランに申し込む" />,
});

async function loader({
  context: { adminClient },
}: LoaderFnContext<Record<never, string>, never, MyRouterContext>) {
  const res = await adminClient.signup_v1_get_upgrade_candidates();
  return {
    adminClient,
    candidates: res.candidates,
    apiKeyGraceDays: res.api_key_grace_days,
  };
}

function PlanSummary({ text }: { text: string }) {
  return (
    <Stack gap="sm">
      <Text size="sm">{text}</Text>
      <Anchor href={PRICING_URL} target="_blank" rel="noreferrer" size="sm">
        料金とプランの詳しい違いを見る
      </Anchor>
    </Stack>
  );
}

function Component() {
  const { adminClient, candidates, apiKeyGraceDays } = Route.useLoaderData();
  const { userInfo } = Route.useRouteContext();
  const [target, setTarget] = useState<string | null>(candidates.length > 0 ? null : NEW_TENANT);
  const [tab, setTab] = useState<string | null>('standard');
  const isDark = useComputedColorScheme('light') === 'dark';
  // Mantine の disabled の面（dark-6 / gray-2）と区切り線（dark-4 / gray-3）が見分けられる面
  const panelBg = isDark ? 'var(--mantine-color-dark-6)' : 'var(--mantine-color-gray-1)';
  const border = '1px solid var(--mantine-color-default-border)';

  const upgrade = useMutation({
    mutationFn: (target: string) =>
      adminClient.signup_v1_upgrade({
        target: target === NEW_TENANT ? NEW_TENANT : { org_id: target },
      }),
    // 送信を待つ間も pending のままにして、ボタンの二度押しで Checkout を二重に作らない
    onSuccess: async ({ url }) => {
      await trackSignUpEvent('begin_checkout', 'upgrade', userInfo);
      window.location.assign(url);
    },
    onError: (error) => {
      logger.error(error);
      notifyError('有料プランへの申し込みに失敗しました。');
    },
  });

  return (
    <MainContainer>
      <PageHeader title="有料プランに申し込む" />
      <Tabs value={tab} onChange={setTab} unstyled maw={880}>
        <Box pos="relative">
          {/* 実線はこの画面から申し込める Free → Standard、破線は問い合わせで進む Provider */}
          <Box
            pos="absolute"
            top={27}
            left="16.67%"
            w="33.33%"
            h={2}
            bg="var(--mantine-primary-color-filled)"
            opacity={0.5}
            style={{ pointerEvents: 'none' }}
          />
          <Box
            pos="absolute"
            top={27}
            left="50%"
            w="33.33%"
            opacity={0.6}
            style={{ borderTop: `2px dashed ${colors.plamoPurple}`, pointerEvents: 'none' }}
          />
          <Tabs.List style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)' }}>
            {plans.map((plan) => {
              const isActive = tab === plan.key;
              // この画面は個人テナント（Free）にだけ出るので、現在のプランは常に Free
              const isCurrent = plan.key === 'free';
              return (
                <Tabs.Tab
                  key={plan.key}
                  value={plan.key}
                  style={{
                    background: isActive ? panelBg : 'transparent',
                    boxShadow: isActive ? `inset 0 3px 0 ${plan.color}` : undefined,
                    border: 0,
                    borderRadius: '12px 12px 0 0',
                    padding: '16px 8px 14px',
                    cursor: 'pointer',
                    color: 'inherit',
                    font: 'inherit',
                    textAlign: 'center',
                  }}
                >
                  <Box
                    mx="auto"
                    w={24}
                    h={24}
                    pos="relative"
                    style={{
                      borderRadius: 12,
                      border: `2px solid ${plan.color}`,
                      background: isCurrent ? plan.color : 'var(--mantine-color-body)',
                    }}
                  />
                  <Text fw={700} mt={8}>
                    {plan.name}
                  </Text>
                  <Text size="sm">{plan.price}</Text>
                  {isCurrent ? (
                    <Text size="xs" fw={700}>
                      現在のプラン
                    </Text>
                  ) : (
                    <Text size="xs" c="dimmed">
                      {plan.audience}
                    </Text>
                  )}
                </Tabs.Tab>
              );
            })}
          </Tabs.List>
        </Box>
        <Box
          bg={panelBg}
          p="xl"
          style={{
            borderRadius:
              tab === 'free' ? '0 12px 12px 12px' : tab === 'provider' ? '12px 0 12px 12px' : 12,
          }}
        >
          <Tabs.Panel value="free">
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xl">
              <PlanSummary text="PLaMo API を試すための無料プランです。利用量とレートリミットに上限があります。" />
              <Box>
                <Text size="sm" c="dimmed">
                  ご利用中のプランです。
                </Text>
                <Anchor component="button" size="sm" onClick={() => setTab('standard')}>
                  Standard を見る
                </Anchor>
              </Box>
            </SimpleGrid>
          </Tabs.Panel>

          <Tabs.Panel value="standard">
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xl">
              <PlanSummary text="本番環境や業務での利用に向けた従量課金のプランです。入出力をモデル学習に使わず、レートリミットも大幅に緩和されます。" />
              <Stack gap="md">
                {candidates.length > 0 && (
                  <Radio.Group value={target} onChange={setTarget} label="申し込み先">
                    <Stack mt="xs" gap="xs">
                      {candidates.map((c) => (
                        <Radio key={c.org_id} value={c.org_id} label={c.name} />
                      ))}
                      <Radio value={NEW_TENANT} label="新しいテナントを作成" />
                    </Stack>
                  </Radio.Group>
                )}
                <Button
                  disabled={!target}
                  loading={upgrade.isPending}
                  onClick={() => target && upgrade.mutate(target)}
                  style={target ? undefined : { border }}
                >
                  Standard に申し込む
                </Button>
                <Stack gap={6}>
                  <Text size="xs" c="dimmed">
                    個人アカウントとは別の組織アカウントで使います。チャット履歴は引き継がれます。
                  </Text>
                  <Text size="xs" c="dimmed">
                    無料プランの API キーは申し込みから {apiKeyGraceDays}{' '}
                    日で使えなくなります。新しいキーに入れ替えてください。
                  </Text>
                </Stack>
              </Stack>
            </SimpleGrid>
          </Tabs.Panel>

          <Tabs.Panel value="provider">
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xl">
              <PlanSummary text="PLaMo を組み込んだ AI サービスを提供する事業者向けのプランです。利用用途の審査があり、料金は個別に見積もります。" />
              <Box>
                <Button
                  component="a"
                  href={CONTACT_FORM_URL}
                  target="_blank"
                  rel="noreferrer"
                  fullWidth
                  color={isDark ? colors.plamoPurple : PROVIDER_BUTTON_LIGHT}
                  c="var(--mantine-primary-color-contrast)"
                >
                  Provider について問い合わせる
                </Button>
              </Box>
            </SimpleGrid>
          </Tabs.Panel>
        </Box>
      </Tabs>
    </MainContainer>
  );
}
